import { BearingEnvelope } from '@paragon/contract-library';
import { LineSegment2D, Point2D } from '@paragon/geometry-library';

import { parseCrowdBuildJson } from './schema';

interface ExtractedLayoutElements {
  bearingEnvelopes: BearingEnvelope[];
}

export function extractLayoutElementsFromCrowdBuildJson(
  crowdBuildJson: string,
  activeLevelGuid: string | null,
): ExtractedLayoutElements {
  const crowdBuild = parseCrowdBuildJson(crowdBuildJson);

  const bearingEnvelopes = crowdBuild.FloorLevels.flatMap((floorLevel) =>
    floorLevel.walls.map((wall): BearingEnvelope => {
      if (wall.wallPoints.length !== 4) {
        throw new Error('Expected 4 wall points.');
      }

      const leftPoint = new Point2D(wall.wallPoints[0].X, wall.wallPoints[0].Y);
      const rightPoint = new Point2D(wall.wallPoints[1].X, wall.wallPoints[1].Y);

      if (leftPoint.equals(rightPoint)) throw new Error('Expected left and right points to be different.');

      const rightExtrudedPoint = new Point2D(wall.wallPoints[2].X, wall.wallPoints[2].Y);
      const leftExtrudedPoint = new Point2D(wall.wallPoints[3].X, wall.wallPoints[3].Y);

      const wallSegment = new LineSegment2D(leftPoint, rightPoint);
      const wallDirection = wallSegment.direction;
      const wallLine = wallSegment.toLine();

      const expectedExtrudedSegment = wallSegment.translate(wallDirection.rotateQuarterTurn().toVector(wall.thickness));

      if (!expectedExtrudedSegment.toLine().containsPoint(rightExtrudedPoint))
        throw new Error('Expected right extruded point to be correct.');
      if (!expectedExtrudedSegment.toLine().containsPoint(leftExtrudedPoint))
        throw new Error('Expected left extruded point to be correct.');

      const distancesAlongLine = [leftPoint, rightPoint, rightExtrudedPoint, leftExtrudedPoint].map((point) =>
        point.projectedSignedDistanceAlong(wallLine),
      );
      const minDistanceAlong = Math.min(...distancesAlongLine);
      const maxDistanceAlong = Math.max(...distancesAlongLine);

      const minPoint = wallLine.getPointAlongLine(minDistanceAlong);
      const maxPoint = wallLine.getPointAlongLine(maxDistanceAlong);

      return {
        guid: crypto.randomUUID(),
        levelGuid: activeLevelGuid,

        name: `${wall.id}`,
        leftPoint: minPoint.toObject(),
        rightPoint: maxPoint.toObject(),
        thickness: wall.thickness,
        numberOfPlies: 1,
        bearingType: 'Wall',
        beamOrientation: 'Drop',
        top: wall.topheight,
        bottom: wall.bottomheight,
        justification: 'Front',
        nonStructural: !wall.isLoadBearing,
      };
    }),
  );

  return { bearingEnvelopes };
}
